<template>
  <div>
    <generic-modal
      :height="OrderListingModalDim.height"
      :width="OrderListingModalDim.width"
      :padding="OrderListingModalDim.padding"
      :modalClass="OrderListingModalDim.modalClass"
      overflowNotNeeded="true"
      v-if="isMyOrdersPage"
    >
      <template v-slot:header>
        <div class="sticky-top bs-none">
          <div
            class="static-pg-header"
            :class="[clientPlatform == 'Web' ? '' : 'border-bottom']"
          >
            <i class="hkfi-back dt-hand" @click="handleBackClick"></i>
            <span class="title">My Orders</span>
          </div>
          <!-- order filters -->
          <div
            class="plr16 pt4 pb12"
            v-if="reorderFilters?.length"
            :class="{ 'oh-wrap': clientPlatform == 'Web' }"
          >
            <div
              class="order-filters flex-row align-items-start align-item-center"
            >
              <div
                v-for="(item, index) in reorderFilters"
                :key="index"
                :class="filterClasses[item.displayText]"
                @click="applyOrderFilter(item, $event, index)"
                :dependentComputedProps="{
                  appliedFilters,
                }"
              >
                {{ item.displayText }}
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:body>
        <div class="pb80 pt40">
          <div
            v-if="previousOrderData?.length > 0"
            :class="[clientPlatform == 'Web' ? 'oh-wrap' : 'plr16']"
          >
            <!-- Order item repeats here -->
            <div v-for="(order, index) in previousOrderData" :key="order?.id">
              <order-item-card
                :data-results="order"
                :activateOrderCardClick="true"
                :index="index"
              ></order-item-card>
            </div>
            <!-- If next set of orders is available -->
            <div v-if="hasNextOrderSet">
              <Observer
                :options="{
                  rootMargin: '0px',
                  threshold: 1.0,
                }"
                @intersect="onLoadingIntersection"
                :keepConnectedOnIntersection="true"
              />
              Loading...
            </div>
          </div>
          <!-- No orders -->
          <div v-if="previousOrderData?.length == 0 && !isLoading">
            <no-order-found
              :data-message="noOrdersProps.msg"
              :data-cta-text="noOrdersProps.ctaText"
              :data-image="staticImgBaseUrl + noOrdersProps.img"
            ></no-order-found>
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <div></div>
      </template>
    </generic-modal>
  </div>
</template>

<script>
import { defineAsyncComponent } from "vue";
import GenericModal from "./GenericModal.vue";
import { staticImgBaseUrl } from "../config/apiEndpoints";
import LocalStorageFunc from "../helpers/localStorageFunc";
import { OrderHistoryPages } from "../helpers/orderHistoryConstants";
import Utility from "../helpers/utility";
import { clientPlatform } from "../config/apiEndpoints";
import OrderItemCard from "./OrderItemCard.vue";
import Observer from "./Observer.vue";
import {
  EVENT_PAGEVIEW_ORDERHISTORYPAGE,
  EVENT_CLICK_ORDER_FILTER,
} from "../helpers/analyticsEvents";

import {
  keyMapsGetter,
  keyMapsAction,
  keyMapsMutation,
} from "../helpers/storeKeys";

export default {
  name: "OrderListing",
  components: {
    GenericModal,
    OrderItemCard,
    NoOrderFound: defineAsyncComponent(() => import("./NoOrderFound.vue")),
    Observer,
  },
  props: {},
  data() {
    return {
      clientPlatform,
      OrderListingModalDim: {
        width: "100vw",
        height: "100vh",
        padding: "0px",
        modalClass: "full-screen-popup order-listing-modal",
      },
      noOrdersProps: {
        msg: "You have not placed any order yet!",
        ctaText: "Explore Menu",
        img: "/images/global/no-order.svg",
      },
      filterClasses: {},
      excludeFilters: ["CURB", "IRCTC", "true"],
      staticImgBaseUrl,
    };
  },
  mounted() {
    this.$store.commit(keyMapsMutation.UPDATE_LOADING_STATE, true);
    setTimeout(
      () => this.$store.commit(keyMapsMutation.UPDATE_LOADING_STATE, false),
      5000
    );
    if (this.isLoggedIn) {
      this.$store.dispatch(keyMapsAction.GET_REORDER_DATA, {
        filterKey: "deliveryType",
        filterValue: "",
      });
    }
    const pathname = window.location.href;
    LocalStorageFunc.setPrevUrl(JSON.stringify(pathname));

    if (LocalStorageFunc.getOrderHistoryPage() == OrderHistoryPages.MY_ORDERS) {
      EVENT_PAGEVIEW_ORDERHISTORYPAGE();
    }
  },
  methods: {
    handleBackClick(e) {
      Utility.redirectToHomeOrRedirectUrl();
    },
    onLoadingIntersection() {
      if (this.$store.state.ReOrder.hasNext) {
        const flts = this.$store.getters[keyMapsGetter.GET_APPLIED_FILTERS];
        let flt = this.reorderFilters.find((x) => x.displayText === flts[0]);
        this.$store.dispatch(keyMapsAction.GET_NEXT_ORDER_BATCH_DATA, {
          filterKey: flt?.filterKey || "deliveryType",
          filterValue: flt?.filterValue || "",
        });
      }
    },
    applyOrderFilter(item, e, index) {
      if (this.appliedFilters.includes(item)) {
        this.$store.commit(keyMapsMutation.UPDATE_APPLIED_FILTERS_AND_SORTS, {
          FILTERS: ["ALL"],
          SORTS: [],
        });
        EVENT_CLICK_ORDER_FILTER({
          category: item.displayText,
          subCategory: "filter unchecked",
          positionWithinSection: index + 1,
        });
      } else {
        this.$store.commit(keyMapsMutation.UPDATE_APPLIED_FILTERS_AND_SORTS, {
          FILTERS: [item.displayText],
          SORTS: [],
        });
        EVENT_CLICK_ORDER_FILTER({
          category: item.displayText,
          subCategory: "filter checked",
          positionWithinSection: index + 1,
        });
      }
      this.$store.dispatch(keyMapsAction.GET_REORDER_DATA, {
        filterKey: item.filterKey,
        filterValue: item.filterValue,
      });
    },
  },
  computed: {
    previousOrderData() {
      return this.$store.state.ReOrder.previousOrderData;
    },
    isMyOrdersPage() {
      return (
        LocalStorageFunc.getOrderHistoryPage() == OrderHistoryPages.MY_ORDERS
      );
    },
    isLoggedIn() {
      return LocalStorageFunc.isLoggedIn();
    },
    isLoading() {
      return this.$store.state.Loader.isLoading;
    },
    hasNextOrderSet() {
      return this.$store.state.ReOrder.hasNext;
    },
    appliedFilters() {
      let flts = this.$store.getters[keyMapsGetter.GET_APPLIED_FILTERS];
      this.reorderFilters.map((flt) => {
        if (flts.includes(flt.displayText)) {
          this.filterClasses[flt.displayText] =
            "order-filter-item  fs-12 lh-18 ml5 selected-filter-item";
        } else {
          this.filterClasses[flt.displayText] =
            "order-filter-item fs-12 lh-18 ml5";
        }
      });
      if (!flts.length) {
        let appliedFlt = this.reorderFilters[0];
        this.filterClasses[appliedFlt.displayText] =
          "order-filter-item  fs-11 lh-18 ml4 selected-filter-item";
      }
      return flts || ["All"];
    },
    reorderFilters() {
      return this.$store.state.ReOrder.reorderFilters?.filter(
        (x) => !this.excludeFilters.includes(x.filterValue)
      );
    },
  },
};
</script>
